import React, { useRef } from 'react';

import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';

import VideoPlayerLayout, { VideoOverlay } from '~/components/VideoPlayerLayout/VideoPlayerLayout';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST, GET_RATING_FOR_ENTITY } from '~/graphql/videos';
import useAuth from '~/hooks/useAuth';
import VideoPlayerNavigation from '~/pages/SingleVideoPage/VideoPlayerNavigation';
import VideoLoggedOutPreview from '~/pages/SingleVideoPage/components/VideoLoggedOutPreview/VideoLoggedOutPreview';
import VideoTitle from '~/pages/SingleVideoPage/components/VideoTitle/VideoTitle';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import useVideoPlayer from '~/pages/SingleVideoPage/hooks/useVideoPlayer';
import useVideoReview from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { MIDDLE_MARGIN_PX } from '~/theme';
import {
  Query,
  QueryGetListReviewsForEntityArgs,
  QueryVideoExistsInUserWatchListArgs,
  ReviewableType,
} from '~/types';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

export const VideoPlayerNewsLayout = ({ videoId }) => {
  const { isAuthenticated } = useAuth();
  const withoutNavigation = useWithoutNavigation();
  const { userInfo, hasAccessToContent } = useUserInfoContext();
  const { tenant } = useTenantContext();

  const playerWrapperRef = useRef<HTMLElement>(null);

  const {
    video,
    goToChapter,
    loadingVideo,
    videoFinished,
    setVideoFinished,
    playerRef,
    verseVideoId,
    pipControls,
  } = useVideoPlayer({ playerWrapperRef, slug: videoId, bumperUrl: tenant?.video_bumper_url });

  const noVideoLoaded = !videoId || loadingVideo;
  const availableForFree = !!video?.free_for_all;

  const videoReview = useVideoReview({
    videoId,
    isXApiPage: withoutNavigation,
  });

  const { data: reviewsList } = useQuery<
    Pick<Query, 'getListReviewsForEntity'>,
    QueryGetListReviewsForEntityArgs
  >(GET_RATING_FOR_ENTITY, {
    variables: {
      entity_type: ReviewableType.Video,
      entity_id: Number(videoId),
      pagination_option: {
        page: 1,
        count_items_on_page: 10,
      },
      include_test_publish_status: true,
    },
    skip: !videoId || !isAuthenticated,
  });

  const { data: existsInWatchlistData } = useQuery<
    Pick<Query, 'VideoExistsInUserWatchList'>,
    QueryVideoExistsInUserWatchListArgs
  >(CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST, {
    variables: {
      filter: {
        user_id: userInfo?.id ? userInfo.id : '',
        video_id: videoId,
      },
    },
    skip: !hasAccessToContent || noVideoLoaded,
    fetchPolicy: 'network-only',
  });

  const rating = reviewsList?.getListReviewsForEntity?.likes_ratio;

  const duration = video?.duration || 0;
  const existsInWatchlist = !!existsInWatchlistData?.VideoExistsInUserWatchList?.exists;

  const playerInfo = (
    <VideoTitle
      existsInWatchlist={existsInWatchlist}
      videoReview={videoReview}
      title={video?.title}
      videoId={videoId}
      duration={duration}
      rating={rating}
      expertId={video?.user?.id}
    />
  );

  return (
    <Wrapper data-testid="newsContentVideo" id={`newsContentVideo-${videoId}`}>
      {(hasAccessToContent || availableForFree) && (
        <VideoPlayerLayout
          loadingVerseVideoId={loadingVideo}
          rating={rating}
          videoReview={videoReview}
          video={video}
          videoFinished={videoFinished}
          pipControls={pipControls}
          playerRef={playerRef}
          verseVideoId={verseVideoId}
          bumperUrl={tenant?.video_bumper_url}
          playerWrapperRef={playerWrapperRef}
          playerInfo={playerInfo}
          renderChildren={({ setShowBumper }) => {
            const replayHandler = (e) => {
              e.stopPropagation();
              goToChapter('0');
              setShowBumper(false);
              setVideoFinished(false);
              sendAnalyticEvent(VIDEO_PAGE_EVENTS.replay);
            };

            if (pipControls.active) {
              return null;
            }

            if (videoFinished) {
              return (
                <VideoOverlay transparent>
                  <VideoPlayerNavigation replayHandler={replayHandler} />
                </VideoOverlay>
              );
            }
            return null;
          }}
          expandAnchorId={`newsContentVideo-${videoId}`}
        />
      )}
      {!hasAccessToContent && !availableForFree && (
        <VideoLoggedOutPreview
          loading={loadingVideo}
          id={videoId}
          title={video?.title}
          previewUrl={video?.preview_url}
          duration={duration}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  margin-top: ${MIDDLE_MARGIN_PX};
`;
